import React, { useState } from 'react';
import { Card, Input, Button, Textarea } from '../ui';
import { Printer, Ticket, Image as ImageIcon, QrCode, Upload, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export function RaffleTicketGenerator() {
  const [orgName, setOrgName] = useState('Local Charity Foundation');
  const [eventName, setEventName] = useState('Annual Fall Gala & Fundraiser');
  const [ticketPrice, setTicketPrice] = useState('$5 Each or 5 for $20');
  const [grandPrize, setGrandPrize] = useState('Grand Prize: 65" 4K Smart TV\n2nd Prize: $500 Gift Card');
  
  const [startNum, setStartNum] = useState(1);
  const [totalTickets, setTotalTickets] = useState(50);
  
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [qrCodeData, setQrCodeData] = useState('');

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setLogoUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Generate an array of numbers to print
  const tickets = Array.from({ length: totalTickets }).map((_, i) => startNum + i);

  // Split tickets into chunks for pages if needed, but flex/grid can handle wrapping.
  // Standard letter size is 8.5" x 11". If we fit 8 tickets per page (2 columns, 4 rows).
  
  const qrUrl = qrCodeData.trim() ? `https://quickchart.io/qr?text=${encodeURIComponent(qrCodeData)}&size=150` : null;

  return (
    <div className="text-left font-sans">
      {/* SCREEN UI - Hidden during print */}
      <div className="print:hidden space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-black flex items-center mb-2 font-display">
              <Ticket className="w-8 h-8 mr-3 text-blue-200" />
              Custom Raffle Tickets
            </h2>
            <p className="text-blue-100 max-w-xl text-lg">
              Design professional, sequentially numbered raffle tickets with tear-away stubs. Ready to print on standard letter paper.
            </p>
          </div>
          <div className="opacity-50 hidden md:block">
            <Ticket className="w-24 h-24" />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6 border-b border-slate-200 dark:border-slate-800 pb-2">Ticket Details</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                    Organization Name
                  </label>
                  <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Local PTO" />
                </div>
                
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                    Event / Drawing Name
                  </label>
                  <Input value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g. Spring Fling" />
                </div>

                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                    Prizes / Description
                  </label>
                  <Textarea value={grandPrize} onChange={(e) => setGrandPrize(e.target.value)} rows={3} placeholder="List prizes here..." />
                </div>

                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                    Ticket Price Text
                  </label>
                  <Input value={ticketPrice} onChange={(e) => setTicketPrice(e.target.value)} placeholder="e.g. $5 Each" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                      Start Number
                    </label>
                    <Input type="number" value={startNum} onChange={(e) => setStartNum(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                      Total Tickets
                    </label>
                    <Input type="number" value={totalTickets} onChange={(e) => setTotalTickets(Number(e.target.value))} max={1000} />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                  <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center">
                    <ImageIcon className="w-4 h-4 mr-2 text-slate-500" /> Branding (Optional)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">
                        Upload Logo (Prints Best in B&W)
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="flex-1 flex items-center justify-center h-10 border border-dashed border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm text-slate-600 dark:text-slate-400 transition-colors">
                          <Upload className="w-4 h-4 mr-2" /> Choose Image
                          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                        </label>
                        {logoUrl && (
                          <Button variant="secondary" onClick={() => setLogoUrl(null)} className="h-10 px-3">Clear</Button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest flex items-center">
                        <QrCode className="w-3 h-3 mr-1" /> QR Code Link (e.g. for rules or donations)
                      </label>
                      <Input value={qrCodeData} onChange={(e) => setQrCodeData(e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700/50">
                  <Button 
                    onClick={handlePrint} 
                    className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold h-12 shadow-lg shadow-blue-500/30"
                  >
                    <Printer size={18} />
                    Print / Download PDF
                  </Button>
                  <p className="text-[10px] text-slate-500 text-center mt-3 uppercase tracking-wider">
                    Tip: Set Margins to "None" in Print Dialog
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Live Preview Container (Screen only) */}
          <div className="lg:col-span-8">
            <div className="sticky top-24">
              <div className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-8 flex justify-center overflow-x-auto shadow-inner h-[800px] overflow-y-auto relative">
                
                <div className="absolute top-4 left-4 bg-black/5 dark:bg-white/5 rounded px-3 py-1 text-xs font-bold text-slate-500 uppercase tracking-widest pointer-events-none">
                  Live Preview (Scale: 70%)
                </div>

                <div className="origin-top scale-[0.7] drop-shadow-2xl bg-white border border-gray-300 pointer-events-none select-none w-[816px] p-[48px] shrink-0 mt-8 relative">
                  {/* Simulate Page Boundaries for Preview - Let's show first 8 tickets (1 page) */}
                  <div className="grid grid-cols-2 w-full bg-white">
                    <AnimatePresence>
                      {tickets.slice(0, 8).map((num, i) => (
                        <motion.div
                          key={num}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ 
                            type: 'spring', 
                            stiffness: 260, 
                            damping: 20, 
                            delay: i * 0.1 // Stagger the animation of tickets
                          }}
                        >
                          <TicketItem 
                            num={num} 
                            orgName={orgName} 
                            eventName={eventName} 
                            ticketPrice={ticketPrice} 
                            grandPrize={grandPrize}
                            logoUrl={logoUrl}
                            qrUrl={qrUrl}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>

              </div>
              <p className="text-center text-sm text-slate-500 mt-4 mb-6">
                Preview shows page 1 (8 tickets). Clicking print will generate all {totalTickets} tickets.
              </p>
              
              {/* Cross-Link to Digital Raffle Drum */}
              <motion.div 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to="/tools/digital-raffle-drum" className="block">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl p-6 text-white shadow-lg flex items-center justify-between">
                    <div>
                      <h4 className="font-black text-xl flex items-center mb-1">
                        <Sparkles className="w-5 h-5 mr-2 text-amber-200" />
                        Looking to draw winners online?
                      </h4>
                      <p className="text-amber-100 text-sm font-medium">
                        Try our animated Digital Raffle Drum for your big screen.
                      </p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-amber-200" />
                  </div>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* PRINT UI - Only visible during print */}
      <div className="hidden print:block print:bg-white print:m-0 print:p-0 z-[9999]">
        <style type="text/css" media="print">
          {`
            @page { 
              size: letter; 
              margin: 0.5in; 
            }
            html, body {
              color-scheme: light !important;
              background: white !important; 
              color: black !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .ticket-page {
              page-break-after: always;
              page-break-inside: avoid;
              display: grid;
              grid-template-columns: 1fr 1fr;
              width: 100%;
            }
            /* Hide the main layout during print */
            #root > header, #root > footer { display: none !important; }
            ::-webkit-scrollbar { display: none; }
          `}
        </style>
        
        {/* Generate pages dynamically */}
        {Array.from({ length: Math.ceil(tickets.length / 8) }).map((_, pageIdx) => (
          <div key={pageIdx} className="ticket-page">
            {tickets.slice(pageIdx * 8, (pageIdx + 1) * 8).map(num => (
              <TicketItem 
                key={num} 
                num={num} 
                orgName={orgName} 
                eventName={eventName} 
                ticketPrice={ticketPrice} 
                grandPrize={grandPrize}
                logoUrl={logoUrl}
                qrUrl={qrUrl}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

interface TicketItemProps {
  num: number;
  orgName: string;
  eventName: string;
  ticketPrice: string;
  grandPrize: string;
  logoUrl: string | null;
  qrUrl: string | null;
}

function TicketItem({ num, orgName, eventName, ticketPrice, grandPrize, logoUrl, qrUrl }: TicketItemProps) {
  const formattedNum = String(num).padStart(4, '0');
  
  return (
    <div className="border border-dashed border-slate-400 bg-white text-black flex flex-row h-[2.25in] relative box-border overflow-hidden print:border-black">
      {/* Background pattern (optional, very light) */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '10px 10px' }}></div>
      
      {/* STUB (Left Side) */}
      <div className="w-[1.25in] border-r-2 border-black border-dashed flex flex-col relative shrink-0 z-10 bg-white">
        <div className="p-2 h-full flex flex-col">
          <div className="text-center font-black text-xs mb-1 uppercase tracking-tighter leading-tight border-b border-black pb-1">
            {eventName || 'RAFFLE'}
          </div>
          <div className="space-y-1 mt-2 flex-grow">
            <div>
              <label className="text-[8px] uppercase tracking-widest block leading-none mb-0.5">Name:</label>
              <div className="border-b border-black w-full mt-2"></div>
            </div>
            <div>
              <label className="text-[8px] uppercase tracking-widest block leading-none mb-0.5">Phone:</label>
              <div className="border-b border-black w-full mt-2"></div>
            </div>
            <div>
              <label className="text-[8px] uppercase tracking-widest block leading-none mb-0.5">Email:</label>
              <div className="border-b border-black w-full mt-2"></div>
            </div>
          </div>
          <div className="mt-auto text-center font-mono font-bold text-lg border-t border-black pt-1">
            № {formattedNum}
          </div>
        </div>
      </div>

      {/* MAIN TICKET (Right Side) */}
      <div className="flex-1 flex flex-col relative z-10 bg-white p-3">
        {/* Ticket Header */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <h3 className="font-black text-sm uppercase tracking-wider text-slate-800 leading-tight">
              {orgName}
            </h3>
            <h2 className="font-black text-xl md:text-2xl font-display uppercase tracking-tighter leading-none mt-0.5">
              {eventName}
            </h2>
          </div>
          <div className="text-right shrink-0 ml-2">
            <div className="font-mono font-bold text-xl text-red-600 bg-red-50 px-2 py-0.5 border border-red-200 rounded">
              № {formattedNum}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex flex-row gap-3 flex-1 mt-1">
          {/* Logo / QR Column */}
          {(logoUrl || qrUrl) && (
            <div className="flex flex-col items-center justify-center gap-2 shrink-0 border-r border-slate-200 pr-3">
              {logoUrl && <img src={logoUrl} alt="Logo" className="w-16 h-16 object-contain grayscale" />}
              {qrUrl && <img src={qrUrl} alt="QR" className="w-16 h-16" />}
            </div>
          )}

          {/* Prizes & Info */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="whitespace-pre-wrap text-sm font-semibold text-slate-800 leading-snug">
              {grandPrize}
            </div>
            
            <div className="mt-auto pt-2">
              <span className="inline-block border-2 border-black rounded px-3 py-1 font-black text-lg">
                {ticketPrice}
              </span>
            </div>
          </div>
        </div>
        
        {/* Footer / Fine Print */}
        <div className="mt-2 text-center border-t border-slate-200 pt-1 text-[8px] uppercase tracking-widest text-slate-500">
          Must be 18 or older to win. Winner need not be present. Non-refundable.
        </div>
      </div>
    </div>
  );
}
